import { useContext } from 'react';
import { Client } from '@api';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { CaretRight, Check, Star } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { resolveClientPersona } from '@/personas/resolveClientPersona';
import { useClientAttention } from './useClientAttention';

/**
 * Handledarens lista över sina brukare.
 *
 * Ett kort per brukare i stället för en tabellrad: namnet är det man letar efter, så
 * det står först och störst, med taggen som dämpad andrarad och personan som en liten
 * etikett. Initialen i ringen ger varje brukare en igenkänningsbar plats i listan —
 * i en lista med tre liknande namn är det formen man hittar på, inte texten.
 *
 * Vald brukare markeras med en bock och en accentram, inte med en ikon utanför kortet:
 * markeringen ska höra ihop med raden den gäller.
 *
 * Brukare som VÄNTAR PÅ ETT BESKED (barnet eller tonåringen har begärt stjärnor för en
 * aktivitet) får en tydlig markering på sitt kort. Poängen är att den syns INNAN man
 * valt någon: väntan är osynlig för brukaren — aktiviteten ger inga stjärnor förrän
 * handledaren svarat — så den som kan lösa det måste se det direkt i listan.
 *
 * Ytan är HELA kortet (en knapp), inte bara texten — träffytan var tidigare en
 * listrad med ikon längst ut, vilket gjorde det lätt att missa på en telefon.
 */

type SupervisorListProps = {
  onSelect?: () => void;
  /**
   * Visa rubriken "Mina brukare". Av i vyer som redan frågar vem man vill stötta —
   * två rubriker ovanpå varandra säger samma sak två gånger.
   */
  showHeading?: boolean;
};

/** Initialen som visas i ringen. Faller tillbaka på taggen för ett namnlöst konto. */
const initialFor = (client: Client): string => {
  const source = client.name?.trim() || client.tag || '';
  return source.charAt(0).toUpperCase();
};

const SupervisorList = ({ onSelect, showHeading = true }: SupervisorListProps) => {
  const { clients, setClient, currentClient } = useContext(SupervisorContext);
  const attention = useClientAttention();
  const { t } = useTranslation();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const handleSelect = (client: Client) => {
    setClient(client);
    if (onSelect) onSelect();
  };

  return (
    <Stack spacing={1.25}>
      {showHeading && (
        <Typography
          variant="caption"
          sx={{
            color: tk.textSoft,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {t('My clients')}
        </Typography>
      )}

      {clients.map((client) => {
        const selected = currentClient?.tag === client.tag;
        const { persona, isFallback } = resolveClientPersona(client.persona);
        const pending = attention[client.tag] ?? 0;
        return (
          <Box
            key={client.tag}
            component="button"
            type="button"
            onClick={() => handleSelect(client)}
            aria-pressed={selected}
            aria-label={
              pending > 0
                ? `${client.name || client.tag}, ${t('supervisor.attention.starValues', {
                    count: pending,
                  })}`
                : client.name || client.tag
            }
            sx={{
              width: '100%',
              textAlign: 'left',
              fontFamily: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: '14px 16px',
              borderRadius: '16px',
              bgcolor: tk.card,
              border: '2px solid',
              borderColor: selected ? 'primary.main' : pending > 0 ? 'warning.main' : tk.border,
              transition: 'transform 0.1s ease, border-color 0.15s ease',
              '&:active': { transform: 'scale(0.99)' },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:active': { transform: 'none' },
              },
            }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Box
                aria-hidden
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '1.15rem',
                  fontWeight: 800,
                }}
              >
                {initialFor(client)}
              </Box>
              {/* Antalet sitter PÅ brukaren, inte i en egen kolumn: det är personen man
                  scannar efter, och märket ska hänga ihop med ansiktet i listan. */}
              {pending > 0 && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    minWidth: 22,
                    height: 22,
                    px: 0.5,
                    borderRadius: '11px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontSize: '0.75rem',
                    fontWeight: 900,
                    border: '2px solid',
                    borderColor: tk.card,
                  }}
                >
                  {pending}
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: tk.text }} noWrap>
                {client.name || client.tag}
              </Typography>
              {/* Vad som väntar står i KLARTEXT under namnet. Ett märke ensamt talar om
                  ATT något finns, inte vad — och det är skillnaden mellan att kunna
                  prioritera och att behöva gå in och titta. */}
              {pending > 0 ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                  <Star size={13} weight="fill" color={tk.text} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'warning.main' }}>
                    {t('supervisor.attention.starValues', { count: pending })}
                  </Typography>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft }} noWrap>
                  {client.tag}
                </Typography>
              )}
            </Box>

            {/* Personan säger vad brukaren möter i appen och styr vad handledaren kan
                administrera. Saknas den visas ingen etikett alls — en gissning här
                hade sett ut som ett faktum. */}
            {!isFallback && (
              <Chip
                size="small"
                label={t(`persona.settings.${persona}`)}
                sx={{
                  flexShrink: 0,
                  bgcolor: tk.secondaryBg,
                  color: tk.textSoft,
                  fontWeight: 600,
                }}
              />
            )}

            {selected ? (
              <Check size={20} weight="bold" color={tk.primary} />
            ) : (
              <CaretRight size={18} weight="bold" color={tk.textSoft} />
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default SupervisorList;
